export type CodeItems = {
  defaultLanguage: string;
  items: CodeItem[];
};
export type CodeItem = {
  group: string;
  examples: LanguageConfig[];
};
export type Example = {
  text: string;
  link: string;
};

export type Code = {
  text?: string;
  quote?: boolean;
  source: string;
};

export type LanguageConfig = {
  language: string;
  prism: string;
  label: string;
  links: Example[];
  code: Code[];
};

export const code: CodeItems = {
  defaultLanguage: 'javascript',
  items: [
    {
      group: 'Languages',
      examples: [
        {
          language: 'csharp',
          prism: 'csharp',
          label: 'CSharp',
          links: [
            {
              text: 'Documentation',
              link: 'https://docs.mailslurp.com/csharp/',
            },
            {
              text: 'Examples',
              link: 'https://docs.mailslurp.com/examples/?q=csharp',
            },
          ],
          code: [
            {
              source:
                '// Run `dotnet add package mailslurp`\r\nvar config = new mailslurp.Client.Configuration();\r\n// create config with x-api-key\r\nconfig.ApiKey.Add("x-api-key", __API_KEY__);\r\n// create new controller instance\r\nvar inboxControllerApi = new mailslurp.Api.InboxControllerApi(config);\r\nvar inbox = inboxControllerApi.CreateInboxWithDefaults();\r\nAssert.IsTrue(inbox.EmailAddress.Contains("@mailslurp"));\r',
            },
          ],
        },
        {
          language: 'go',
          prism: 'go',
          label: 'Go',
          links: [
            { text: 'Documentation', link: 'https://docs.mailslurp.com/go/' },
            {
              text: 'Examples',
              link: 'https://docs.mailslurp.com/examples/?q=go',
            },
          ],
          code: [
            {
              source:
                '// install from https://github.com/mailslurp/mailslurp-client-go\nctx := context.WithValue(context.Background(), mailslurp.ContextAPIKey, mailslurp.APIKey{Key: __API_KEY__})\n\n// create mailslurp client\nclient := mailslurp.NewAPIClient(mailslurp.NewConfiguration())\n\n// create an inbox\ninbox, _, err := client.InboxControllerApi.CreateInbox(ctx, &mailslurp.CreateInboxOpts{})',
            },
          ],
        },
        {
          language: 'java',
          prism: 'java',
          label: 'Java',
          links: [
            { text: 'Documentation', link: 'https://docs.mailslurp.com/java/' },
            {
              text: 'Examples',
              link: 'https://docs.mailslurp.com/examples/?q=java',
            },
          ],
          code: [
            {
              source:
                '// install from https://central.sonatype.com/artifact/com.mailslurp/mailslurp-client-java/\nApiClient client = Configuration.getDefaultApiClient();\nclient.setApiKey(__API_KEY__);\n// create an inbox\nInboxControllerApi inboxControllerApi = new InboxControllerApi(client);\nInboxDto inbox = inboxControllerApi.createInboxWithDefaults();',
            },
          ],
        },
        {
          language: 'javascript',
          prism: 'typescript',
          label: 'Javascript',
          links: [
            { text: 'Documentation', link: 'https://docs.mailslurp.com/js/' },
            {
              text: 'Examples',
              link: 'https://docs.mailslurp.com/examples/?q=javascript',
            },
          ],
          code: [
            {
              source:
                "// npm i --save mailslurp-client\n// import { MailSlurp } from 'mailslurp-client';\nconst mailslurp = new MailSlurp({ apiKey: __API_KEY__ });\nconst inbox = await mailslurp.inboxController.createInboxWithDefaults();",
            },
          ],
        },
        {
          language: 'php',
          prism: 'php',
          label: 'PHP',
          links: [
            { text: 'Documentation', link: 'https://docs.mailslurp.com/php/' },
            {
              text: 'Examples',
              link: 'https://docs.mailslurp.com/examples/?q=php',
            },
          ],
          code: [
            {
              source:
                "// install via https://packagist.org/packages/mailslurp/mailslurp-client-php\n// configure client with api key\n$config = MailSlurp\\Configuration::getDefaultConfiguration()\n    ->setApiKey('x-api-key', __API_KEY__);\n\n// create an inbox controller with config\n$inboxController = new MailSlurp\\Apis\\InboxControllerApi(null, $config);\n$inbox = $inboxController->createInbox();",
            },
          ],
        },
        {
          language: 'python',
          prism: 'python',
          label: 'Python',
          code: [
            {
              source:
                "# pip install mailslurp-client\nimport mailslurp_client\n# create a mailslurp configuration\nconfiguration = mailslurp_client.Configuration()\nconfiguration.api_key['x-api-key'] = __API_KEY__\nwith mailslurp_client.ApiClient(configuration) as api_client:\n    # create an inbox\n    inbox_controller = mailslurp_client.InboxControllerApi(api_client)\n    inbox = inbox_controller.create_inbox()",
            },
          ],
          links: [
            {
              text: 'Documentation',
              link: 'https://docs.mailslurp.com/python/',
            },
            {
              text: 'Examples',
              link: 'https://docs.mailslurp.com/examples/?q=python',
            },
          ],
        },
        {
          language: 'ruby',
          prism: 'ruby',
          label: 'Ruby',
          code: [
            {
              source:
                "# install gem https://rubygems.org/gems/mailslurp_client\nMailSlurpClient.configure do |config|\n  config.api_key['x-api-key'] = __API_KEY__\nend\n# create inboxes\ninbox_controller = MailSlurpClient::InboxControllerApi.new\ninbox_1 = inbox_controller.create_inbox",
            },
          ],
          links: [
            { text: 'Documentation', link: 'https://docs.mailslurp.com/ruby/' },
            {
              text: 'Examples',
              link: 'https://docs.mailslurp.com/examples/?q=ruby',
            },
          ],
        },
      ],
    },
    {
      group: 'Testing',
      examples: [
        {
          language: 'cypress',
          prism: 'javascript',
          label: 'Cypress JS',
          links: [
            {
              text: 'Documentation',
              link: 'https://docs.mailslurp.com/cypress-mailslurp/',
            },
            {
              text: 'Examples',
              link: 'https://docs.mailslurp.com/examples/?q=cypress',
            },
          ],
          code: [
            {
              text: 'First install Cypress then add the MailSlurp plugin.',
              source: 'npm install --save-dev cypress-mailslurp',
            },
            {
              text: 'Then include the plugin in your `cypress/support/index.{js,ts}` file.',
              source: "import 'cypress-mailslurp';",
            },
            {
              text: 'Configure `cypress.env.json` with your MailSlurp API key.',
              source: '{ "MAILSLURP_API_KEY": "__API_KEY__" }',
            },
            {
              text: 'Access MailSlurp instance with `cy.mailslurp()`.',
              source:
                "// use cypress-mailslurp plugin to create an email address before test\n// note you MUST import `cypress-mailslurp` in your `cypress/support/index.{js,ts}` file.\n\nbefore(function () {\n    cy.log(\"Wrap inbox before test\")\n    return cy.mailslurp()\n        .then(mailslurp => mailslurp.createInbox())\n        .then(inbox => {\n            cy.log(`Inbox id ${inbox.id}`)\n            // save inbox id and email address to this (make sure you use function and not arrow syntax)\n            cy.wrap(inbox.id).as('inboxId')\n            cy.wrap(inbox.emailAddress).as('emailAddress')\n        })\n});",
            },
          ],
        },
      ],
    },
    {
      group: 'Shell',
      examples: [
        {
          language: 'curl',
          label: 'Curl',
          prism: 'sh',
          code: [
            {
              source:
                "curl -H 'x-api-key:__API_KEY__'\\\n -X POST api.mailslurp.com/inboxes/withDefaults",
              quote: false,
            },
          ],
          links: [
            { text: 'Documentation', link: 'https://docs.mailslurp.com/api/' },
            { text: 'Examples', link: 'https://docs.mailslurp.com/examples/' },
          ],
        },
      ],
    },
  ],
};
